/**
 * Entity Management — the FINDER for entities.
 *
 * ENTITY_WORKBENCH_PLAN.md W0 (2026-08-12): this page finds a company and
 * confirms it is the right one. It does not change it. Every edit surface —
 * editorial overrides, LinkedIn tag, source mappings, enrichment, deletion,
 * commissions — moved to the workbench's Manage tab, so an entity has one
 * place it can be changed and not two (R18).
 *
 * - Table with search and filters (entity_type, group, sector)
 * - Expander = compact, read-only confirmation panel + "Open workbench"
 * - Create entity modal
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Search, Plus, ChevronDown, ChevronRight, X, Loader2, ArrowRight, Sparkles,
} from 'lucide-react';
import {
  listEntities, getEntity, createEntity, lookupEntity,
} from '../lib/api/entities.js';
import type {
  EntitySummary, EntityDetail, EntityCreateRequest, EntityListParams,
} from '../lib/api/types.js';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../components/ui/modal.js';
import { TableScroll } from '../components/ui/table-scroll.js';

const ENTITY_TYPES = ['asx_company', 'nsx_company', 'lse_stock', 'tsx_stock', 'sector', 'keyword', 'custom', 'company'];
const SECTORS = ['Industry Majors', 'Gold & Precious Metals', 'Base Metals', 'Battery Metals', 'Critical Minerals', 'Iron Ore', 'Energy', 'Markets & Finance', 'Technology', 'Biotech & Health'];
const SORT_OPTIONS = [
  { value: 'canonical_identifier', label: 'Ticker (A-Z)' },
  { value: 'display_name', label: 'Name (A-Z)' },
  { value: 'created_at', label: 'Newest first' },
];

export function EntitiesPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [groupFilter, setGroupFilter] = useState<string>('');
  const [sectorFilter, setSectorFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('canonical_identifier');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [page, setPage] = useState(1);

  // Load entity groups for the filter dropdown
  const { data: groups } = useQuery({
    queryKey: ['entity-groups-filter'],
    queryFn: async () => {
      const { listEntityGroups } = await import('../lib/api/ingress.js');
      return listEntityGroups(true);
    },
  });

  const params: EntityListParams = {
    page,
    page_size: 50,
    search: search || undefined,
    entity_type: typeFilter as any || undefined,
    group_id: groupFilter || undefined,
    sector: sectorFilter || undefined,
    sort_by: sortBy,
  };

  const entitiesQuery = useQuery({
    queryKey: ['entities', params],
    queryFn: () => listEntities(params),
  });

  const entities = entitiesQuery.data?.items ?? [];
  const totalPages = entitiesQuery.data?.pages ?? 1;

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-grey-900">Entities</h1>
          <p className="text-sm text-grey-500 mt-1">
            {entitiesQuery.data?.total ?? 0} entities
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          New Entity
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
          <input
            type="text"
            placeholder="Search ticker or name..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2 border border-grey-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <select
          value={typeFilter}
          onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-grey-200 rounded-lg text-sm bg-surface"
        >
          <option value="">All types</option>
          {ENTITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          value={groupFilter}
          onChange={e => { setGroupFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-grey-200 rounded-lg text-sm bg-surface"
        >
          <option value="">All groups</option>
          {(groups || []).map((g: any) => <option key={g.group_id} value={g.group_id}>{g.group_name}</option>)}
        </select>
        <select
          value={sectorFilter}
          onChange={e => { setSectorFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-grey-200 rounded-lg text-sm bg-surface"
        >
          <option value="">All sectors</option>
          {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={sortBy}
          onChange={e => { setSortBy(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-grey-200 rounded-lg text-sm bg-surface"
        >
          {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-surface rounded-xl border border-grey-200 overflow-hidden">
        {entitiesQuery.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 text-grey-400 animate-spin" />
          </div>
        ) : entities.length === 0 ? (
          <div className="text-center py-12 text-grey-500">
            No entities found
          </div>
        ) : (
          <>
            {/* md+: the familiar table */}
            <TableScroll className="hidden md:block">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-grey-100 bg-grey-50">
                    <th className="text-left text-xs font-medium text-grey-500 uppercase px-4 py-3 w-8"></th>
                    <th className="text-left text-xs font-medium text-grey-500 uppercase px-4 py-3">Ticker</th>
                    <th className="text-left text-xs font-medium text-grey-500 uppercase px-4 py-3">Name</th>
                    <th className="text-left text-xs font-medium text-grey-500 uppercase px-4 py-3">Type</th>
                    <th className="text-left text-xs font-medium text-grey-500 uppercase px-4 py-3">Approval</th>
                    <th className="text-left text-xs font-medium text-grey-500 uppercase px-4 py-3">Announcements</th>
                    <th className="text-left text-xs font-medium text-grey-500 uppercase px-4 py-3">Created</th>
                    <th className="px-4 py-3 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {entities.map(entity => (
                    <EntityRow
                      key={entity.entity_id}
                      entity={entity}
                      isExpanded={expandedId === entity.entity_id}
                      onToggle={() => setExpandedId(expandedId === entity.entity_id ? null : entity.entity_id)}
                    />
                  ))}
                </tbody>
              </table>
            </TableScroll>
            {/* phones: stacked cards, same rows + expand panel */}
            <div className="md:hidden divide-y divide-grey-100">
              {entities.map(entity => (
                <EntityCard
                  key={entity.entity_id}
                  entity={entity}
                  isExpanded={expandedId === entity.entity_id}
                  onToggle={() => setExpandedId(expandedId === entity.entity_id ? null : entity.entity_id)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-1 text-sm border border-grey-200 rounded-lg disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-sm text-grey-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-1 text-sm border border-grey-200 rounded-lg disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateEntityModal onClose={() => setShowCreateModal(false)} />
      )}
    </div>
  );
}


// ============================================================================
// Entity Row with expandable detail panel
// ============================================================================

function EntityRow({ entity, isExpanded, onToggle }: {
  entity: EntitySummary;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        onClick={onToggle}
        className="border-b border-grey-50 hover:bg-grey-25 cursor-pointer transition-colors"
      >
        <td className="px-4 py-3">
          {isExpanded
            ? <ChevronDown className="h-4 w-4 text-grey-400" />
            : <ChevronRight className="h-4 w-4 text-grey-400" />
          }
        </td>
        <td className="px-4 py-3">
          <Link
            to={`/entities/${entity.entity_id}`}
            onClick={(e) => e.stopPropagation()}
            className="font-mono font-semibold text-sm text-primary-700 hover:underline"
            title="Open entity workbench"
          >
            {entity.canonical_identifier}
          </Link>
        </td>
        <td className="px-4 py-3 text-sm text-grey-700">{entity.display_name}</td>
        <td className="px-4 py-3">
          <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700">
            {entity.entity_type}
          </span>
        </td>
        <td className="px-4 py-3">
          <StatusBadge status={entity.approval_status} />
        </td>
        <td className="px-4 py-3 text-sm text-grey-600">{entity.announcement_count}</td>
        <td className="px-4 py-3 text-xs text-grey-500">
          {format(new Date(entity.created_at), 'dd MMM yyyy')}
        </td>
        <td className="px-4 py-3 text-right">
          <Link
            to={`/entities/${entity.entity_id}`}
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-grey-200 text-grey-700 hover:bg-grey-50 hover:text-primary-700 hover:border-primary-200"
            title="Open the entity workbench"
          >
            View <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={8} className="bg-grey-25 px-4 py-0">
            <EntityConfirmPanel entity={entity} />
          </td>
        </tr>
      )}
    </>
  );
}


// Phone card — same fields as EntityRow, stacked; expands the same panel.
function EntityCard({ entity, isExpanded, onToggle }: {
  entity: EntitySummary;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div>
      <div className="flex items-start gap-2 px-4 py-3 hover:bg-grey-25 transition-colors">
        <button onClick={onToggle} className="flex-1 min-w-0 text-left flex items-start gap-3">
          <span className="mt-0.5 flex-shrink-0">
            {isExpanded
              ? <ChevronDown className="h-4 w-4 text-grey-400" />
              : <ChevronRight className="h-4 w-4 text-grey-400" />
            }
          </span>
          <span className="flex-1 min-w-0">
            <span className="flex items-center gap-2 flex-wrap">
              <span className="font-mono font-semibold text-sm text-grey-900">
                {entity.canonical_identifier}
              </span>
              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700">
                {entity.entity_type}
              </span>
              <StatusBadge status={entity.approval_status} />
            </span>
            <span className="block text-sm text-grey-700 mt-0.5">{entity.display_name}</span>
            <span className="block text-xs text-grey-500 mt-0.5">
              {entity.announcement_count} announcement{entity.announcement_count === 1 ? '' : 's'} · {format(new Date(entity.created_at), 'dd MMM yyyy')}
            </span>
          </span>
        </button>
        <Link
          to={`/entities/${entity.entity_id}`}
          className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg border border-grey-200 text-grey-700 active:bg-grey-50"
        >
          View <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      {isExpanded && (
        <div className="bg-grey-25 px-4">
          <EntityConfirmPanel entity={entity} />
        </div>
      )}
    </div>
  );
}


// ============================================================================
// Entity Confirm Panel — read-only (W0 / R18)
// ============================================================================
// "Is this the right company?" and "what do we hold on it?" — nothing more.
// Every edit surface this panel used to carry now lives at
// /entities/:id?tab=manage, so an entity is changed in exactly one place.

function EntityConfirmPanel({ entity }: { entity: EntitySummary }) {
  const detailQuery = useQuery({
    queryKey: ['entity', entity.entity_id],
    queryFn: () => getEntity(entity.entity_id),
  });

  const detail = detailQuery.data as EntityDetail | undefined;
  const meta = detail?.metadata ?? {};
  const stats = detail?.statistics;

  const identity: [string, string | undefined][] = [
    ['Exchange', meta.exchange ?? meta.exchange_code],
    ['Ticker', detail?.canonical_identifier ?? entity.canonical_identifier],
    ['Type', detail?.entity_type ?? entity.entity_type],
    ['Sector', meta.sector],
    ['GICS sector', meta.gics_sector],
    ['Market cap', meta.market_cap_tier],
    ['Domain', meta.canonical_domain ?? meta.website_url],
  ];

  return (
    <div className="py-4 space-y-3">
      {detailQuery.isLoading && (
        <div className="py-6 flex items-center justify-center">
          <Loader2 className="h-5 w-5 text-grey-400 animate-spin" />
        </div>
      )}
      {detailQuery.isError && (
        <div className="py-6 text-sm text-red-700">
          Failed to load entity details: {(detailQuery.error as Error)?.message ?? 'Unknown error'}
        </div>
      )}

      {detail && (
        <>
          <div className="bg-surface rounded-lg border border-grey-200 p-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-bold text-grey-900">
                    {(meta.exchange ?? meta.exchange_code ?? '') && `${meta.exchange ?? meta.exchange_code}:`}
                    {detail.canonical_identifier}
                  </span>
                  <span className="text-sm font-semibold text-grey-900">{detail.display_name}</span>
                  <StatusBadge status={detail.approval_status} />
                </div>
                {detail.description && (
                  <p className="text-sm text-grey-600 mt-2 max-w-3xl">{detail.description}</p>
                )}
              </div>
              <Link
                to={`/entities/${entity.entity_id}`}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Open workbench <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1">
              {identity
                .filter(([, value]) => value)
                .map(([label, value]) => (
                  <div key={label} className="flex gap-2 min-w-0 text-xs">
                    <dt className="shrink-0 text-grey-400">{label}</dt>
                    <dd className="text-grey-700 min-w-0 truncate" title={String(value)}>{String(value)}</dd>
                  </div>
                ))}
            </dl>
          </div>

          <div className="bg-surface rounded-lg border border-grey-200 p-4">
            <h4 className="text-xs font-semibold text-grey-500 uppercase mb-2">What we hold</h4>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <Stat label="Announcements" value={stats?.announcement_count ?? entity.announcement_count} />
              <Stat label="Articles" value={stats?.article_count ?? 0} />
              <Stat label="Published" value={stats?.article_published_count ?? 0} />
              <Stat label="Contact channels" value={stats?.contact_channel_count ?? 0} />
              <Stat label="Source mappings" value={detail.source_mappings.length} />
            </div>
            {stats?.last_announcement_title && (
              <p className="text-xs text-grey-500 mt-3 truncate">
                Last: {stats.last_announcement_title}
                {stats.last_announcement_date && ` · ${format(new Date(stats.last_announcement_date), 'dd MMM yyyy')}`}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div className="text-lg font-semibold text-grey-900">{value}</div>
      <div className="text-xs text-grey-500">{label}</div>
    </div>
  );
}

// ============================================================================
// Status Badge
// ============================================================================

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    approved: 'bg-green-50 text-green-700',
    pending: 'bg-amber-50 text-amber-700',
    rejected: 'bg-grey-100 text-grey-500',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${styles[status] || styles.pending}`}>
      {status}
    </span>
  );
}


// ============================================================================
// Create Entity Modal
// ============================================================================

function CreateEntityModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLooking, setIsLooking] = useState(false);
  const [discovered, setDiscovered] = useState<Record<string, any> | null>(null);

  // Form fields — populated by discovery or manual entry
  const [entityType, setEntityType] = useState('asx_company');
  const [ticker, setTicker] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [sector, setSector] = useState('');
  const [gicsSector, setGicsSector] = useState('');
  const [marketCapTier, setMarketCapTier] = useState('');

  const createMut = useMutation({
    mutationFn: (data: EntityCreateRequest) => createEntity(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['entities'] }); onClose(); },
  });

  const handleLookup = async () => {
    if (!searchQuery.trim()) return;
    setIsLooking(true);
    try {
      const result = await lookupEntity(searchQuery.trim());
      setDiscovered(result);
      // Auto-fill form from discovery
      if (result.ticker) setTicker(result.ticker);
      if (result.display_name) setDisplayName(result.display_name);
      if (result.description) setDescription(result.description);
      if (result.website_url) setWebsiteUrl(result.website_url);
      if (result.sector) setSector(result.sector);
      if (result.gics_sector) setGicsSector(result.gics_sector);
      if (result.market_cap_tier) setMarketCapTier(result.market_cap_tier);
      if (result.entity_type) setEntityType(result.entity_type);
    } catch (e) {
      // Lookup failed — operator enters manually
    } finally {
      setIsLooking(false);
    }
  };

  const handleCreate = () => {
    const metadata: Record<string, any> = {};
    if (sector) metadata.sector = sector;
    if (gicsSector) metadata.gics_sector = gicsSector;
    if (marketCapTier) metadata.market_cap_tier = marketCapTier;
    if (websiteUrl) metadata.website_url = websiteUrl;
    if (discovered?.exchange) metadata.exchange = discovered.exchange;
    if (discovered?.market_cap) metadata.market_cap = discovered.market_cap;
    if (discovered?.regions) metadata.regions = discovered.regions;
    if (discovered?.industry_group) metadata.industry_group = discovered.industry_group;

    createMut.mutate({
      entity_type: entityType as any,
      canonical_identifier: ticker,
      display_name: displayName,
      description,
      metadata,
    });
  };

  return (
    <Modal onClose={onClose} size="lg">
      <ModalHeader>
        <h2 className="text-lg font-semibold text-grey-900">New Entity</h2>
        <button onClick={onClose} className="text-grey-400 hover:text-grey-600 touch-target"><X className="h-5 w-5" /></button>
      </ModalHeader>

      <ModalBody className="space-y-4">
          {/* Lookup bar */}
          <div>
            <label className="block text-sm font-medium text-grey-700 mb-1">Search</label>
            <div className="flex gap-2">
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleLookup()}
                placeholder="Ticker, company name, or URL..."
                className="flex-1 border border-grey-200 rounded-lg px-3 py-2 text-sm"
              />
              <button onClick={handleLookup} disabled={isLooking || !searchQuery.trim()}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
                {isLooking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {isLooking ? 'Looking...' : 'Discover'}
              </button>
            </div>
            {discovered && (
              <p className="text-xs text-grey-400 mt-1">
                Found via: {discovered.sources_checked?.join(', ') || 'unknown'}
              </p>
            )}
          </div>

          {/* Form fields — populated by discovery */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-grey-600 mb-1">Ticker</label>
              <input value={ticker} onChange={e => setTicker(e.target.value.toUpperCase())}
                placeholder="BHP" className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-grey-600 mb-1">Type</label>
              <select value={entityType} onChange={e => setEntityType(e.target.value)}
                className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm bg-surface">
                {ENTITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-grey-600 mb-1">Display Name</label>
            <input value={displayName} onChange={e => setDisplayName(e.target.value)}
              placeholder="BHP Group Limited" className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm" />
          </div>

          <div>
            <label className="block text-xs font-medium text-grey-600 mb-1">Description</label>
            <input value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Global resources company" className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm" />
          </div>

          <div>
            <label className="block text-xs font-medium text-grey-600 mb-1">Website URL</label>
            <input value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)}
              placeholder="https://www.bhp.com" className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-grey-600 mb-1">Sector</label>
              <input value={sector} onChange={e => setSector(e.target.value)}
                placeholder="Industry Majors" className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-grey-600 mb-1">GICS Sector</label>
              <input value={gicsSector} onChange={e => setGicsSector(e.target.value)}
                placeholder="Materials" className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-grey-600 mb-1">Market Cap</label>
              <select value={marketCapTier} onChange={e => setMarketCapTier(e.target.value)}
                className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm bg-surface">
                <option value="">Auto</option>
                {['mega_cap', 'large_cap', 'mid_cap', 'small_cap', 'micro_cap'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {createMut.isError && (
            <p className="text-sm text-red-600">{(createMut.error as any)?.message || 'Failed to create entity'}</p>
          )}
      </ModalBody>

      <ModalFooter>
        <button onClick={onClose} className="px-4 py-2 text-sm text-grey-600 hover:text-grey-800">Cancel</button>
        <button onClick={handleCreate} disabled={createMut.isPending || !ticker || !displayName}
          className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
          {createMut.isPending ? 'Creating...' : 'Create Entity'}
        </button>
      </ModalFooter>
    </Modal>
  );
}

